import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";
import { Suspense, lazy, useState, useEffect, useCallback, useRef } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import {
  RoutePlanDrawer,
  DRAWER_WIDTH,
} from "./components/RoutePlan/RoutePlanDrawer";
import { LanguageProvider } from "./i18n/LanguageProvider";
import { FairwayRoutingProvider } from "./features/fairwayRouting/FairwayRoutingContext";
import { VesselSettingsProvider } from "./features/vesselSettings/VesselSettingsContext";
import { RoutePlanProvider } from "./features/routePlan/RoutePlanContext";
import { MapProvider, useMap } from "./features/map/MapContext";
import { PlaceSearchProvider } from "./features/placeSearch/PlaceSearchContext";
import { ReachableAreaProvider } from "./features/reachableArea/ReachableAreaContext";
import { ChartSettingsProvider } from "./features/chartSettings/ChartSettingsContext";
import { SplashScreen } from "./components/SplashScreen";
import { ErrorBoundary } from "./components/ErrorBoundary/ErrorBoundary";
import { WebGLError } from "./components/WebGLError/WebGLError";
import { checkWebGLAvailability } from "./utils/webglCheck";
import { AuthProvider } from "./features/auth/AuthContext";
import { startLoadingFairwayData } from "./utils/pathfinding/fairwayRouting";
import { AppSnackbarProvider } from "./components/Snackbar/AppSnackbarProvider";
import {
  AnalyticsConsentProvider,
  useAnalyticsConsent,
} from "./features/analyticsConsent/AnalyticsConsentContext";
import { ConsentBanner } from "./components/AnalyticsConsent/ConsentBanner";
import { trackPageView } from "./utils/analytics";
import { UiOverlaysProvider, useUiOverlays } from "./features/uiOverlays/UiOverlaysContext";
import { DevSettingsProvider } from "./features/dev/DevSettingsContext";
import { DevConsoleModal } from "./features/dev/DevConsoleModal";

const MapView = lazy(() => import("./components/Map/MapView"));
const AccountPage = lazy(() => import("./components/Account/AccountPage"));
const SubscriptionPage = lazy(
  () => import("./components/Subscription/SubscriptionPage"),
);

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#1C416A",
    },
    secondary: {
      main: "#dc004e",
    },
  },
  typography: {
    fontFamily: [
      "Finlandica",
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
    ].join(","),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "10px",
          textTransform: "none",
          fontWeight: 400,
          boxShadow: "none",
          padding: "12px 24px",
          "&:hover": {
            boxShadow: "none",
          },
          "&:focus": {
            boxShadow: "none",
          },
        },
        contained: {
          backgroundColor: "#1C416A",
          color: "#FFFFFF",
          border: "none",
          "&:hover": {
            backgroundColor: "#1C416A",
            boxShadow: "none",
          },
          "&:focus": {
            boxShadow: "none",
          },
          "&:active": {
            boxShadow: "none",
          },
        },
        outlined: {
          borderColor: "#1C416A",
          color: "#1C416A",
          "&:hover": {
            borderColor: "#1C416A",
            backgroundColor: "rgba(28, 65, 106, 0.08)",
            boxShadow: "none",
          },
        },
        text: {
          color: "#1C416A",
          "&:hover": {
            backgroundColor: "rgba(28, 65, 106, 0.08)",
            boxShadow: "none",
          },
        },
      },
    },
  },
});

function AppLayout() {
  const { mapImagesLoaded } = useMap();
  const [desktopDrawerOpen, setDesktopDrawerOpen] = useState(true);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [devConsoleOpen, setDevConsoleOpen] = useState(false);
  const startDemoRef = useRef<(() => void) | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isFullScreenRoute = ["/account", "/subscription"].includes(
    location.pathname,
  );
  const isMapRoute = location.pathname === "/";

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  const handleGlobalKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.defaultPrevented) return;
    const target = event.target as HTMLElement | null;
    if (target) {
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) {
        return;
      }
    }
    const isCtrlD =
      (event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey && event.key.toLowerCase() === "d";
    if (!isCtrlD) return;
    event.preventDefault();
    setDevConsoleOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => {
      window.removeEventListener("keydown", handleGlobalKeyDown);
    };
  }, [handleGlobalKeyDown]);

  useEffect(() => {
    if (!location.search) return;
    const params = new URLSearchParams(location.search);
    const action = params.get("action")?.toLowerCase();
    const response = params.get("response");
    if (action !== "payment" || !response) return;
    if (location.pathname === "/subscription") return;
    navigate(
      { pathname: "/subscription", search: location.search },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate]);

  return (
    <>
      <SplashScreen visible={isMapRoute && !mapImagesLoaded} />
      <DevConsoleModal
        open={devConsoleOpen}
        onClose={() => setDevConsoleOpen(false)}
      />
      <Box sx={{ display: "flex", height: "100vh", width: "100vw" }}>
        {!isFullScreenRoute && (
          <RoutePlanDrawer
            mobileOpen={mobileDrawerOpen}
            onMobileToggle={setMobileDrawerOpen}
            desktopOpen={desktopDrawerOpen}
            onDesktopToggle={setDesktopDrawerOpen}
            hideDesktopToggleHandle
            hideMobileToggleHandle
            onStartDemo={() => startDemoRef.current?.()}
          />
        )}
        <Box
          component="main"
          sx={{
            p: 0,
            width: {
              xs: "100%",
              md: isFullScreenRoute
                ? "100%"
                : desktopDrawerOpen
                  ? `calc(100% - ${DRAWER_WIDTH}px)`
                  : "100%",
            },
            height: "100%",
            overflowY: isFullScreenRoute ? "auto" : "hidden",
            transition: "width 0.3s ease-in-out",
          }}
        >
          <Suspense
            fallback={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  height: "100%",
                }}
              >
                <CircularProgress />
              </Box>
            }
          >
            <Routes>
              <Route
                path="/"
                element={
                  <MapView
                    aisEnabled={true}
                    drawerWidth={DRAWER_WIDTH}
                    drawerOpen={desktopDrawerOpen}
                    onRequestCloseRouteDrawer={() =>
                      setDesktopDrawerOpen(false)
                    }
                    onRequestToggleRouteDrawer={() =>
                      setDesktopDrawerOpen((prev) => !prev)
                    }
                    onRequestSetRouteDrawerOpen={setDesktopDrawerOpen}
                    mobileDrawerOpen={mobileDrawerOpen}
                    onRequestCloseRouteDrawerMobile={() =>
                      setMobileDrawerOpen(false)
                    }
                    onRequestToggleRouteDrawerMobile={() =>
                      setMobileDrawerOpen((prev) => !prev)
                    }
                    onRequestSetRouteDrawerOpenMobile={setMobileDrawerOpen}
                    onOpenAccount={() => navigate("/account")}
                    onRegisterStartDemo={(startDemo) => {
                      startDemoRef.current = startDemo;
                    }}
                  />
                }
              />
              <Route path="/account" element={<AccountPage />} />
              <Route
                path="/subscription"
                element={<SubscriptionPage onBack={() => navigate("/")} />}
              />
            </Routes>
          </Suspense>
        </Box>
      </Box>
    </>
  );
}

function App() {
  const [webglAvailable, setWebglAvailable] = useState<boolean | null>(null);

  useEffect(() => {
    // Check WebGL availability on mount
    const { available } = checkWebGLAvailability();
    setWebglAvailable(available);
  }, []);

  useEffect(() => {
    // Start loading fairway data in the background (non-blocking)
    startLoadingFairwayData();
  }, []);

  // Show loading state while checking WebGL
  if (webglAvailable === null) {
    return (
      <LanguageProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100vw",
              height: "100vh",
            }}
          >
            <CircularProgress />
          </Box>
        </ThemeProvider>
      </LanguageProvider>
    );
  }

  // Show WebGL error if not available
  if (!webglAvailable) {
    return (
      <LanguageProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <WebGLError />
        </ThemeProvider>
      </LanguageProvider>
    );
  }

  // Normal app flow
  return (
    <LanguageProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AnalyticsConsentProvider>
          <UiOverlaysProvider>
            <DevSettingsProvider>
              <AnalyticsConsentBannerGate />
              <ErrorBoundary>
                <AppSnackbarProvider>
                  <AuthProvider>
                    <ChartSettingsProvider>
                      <VesselSettingsProvider>
                        <RoutePlanProvider>
                          <FairwayRoutingProvider>
                            <MapProvider>
                              <PlaceSearchProvider>
                                <ReachableAreaProvider>
                                  <AppLayout />
                                </ReachableAreaProvider>
                              </PlaceSearchProvider>
                            </MapProvider>
                          </FairwayRoutingProvider>
                        </RoutePlanProvider>
                      </VesselSettingsProvider>
                    </ChartSettingsProvider>
                  </AuthProvider>
                </AppSnackbarProvider>
              </ErrorBoundary>
            </DevSettingsProvider>
          </UiOverlaysProvider>
        </AnalyticsConsentProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
}

function AnalyticsConsentBannerGate() {
  const { showBanner } = useAnalyticsConsent();
  const { blockingOverlayOpen } = useUiOverlays();
  if (!showBanner || blockingOverlayOpen) return null;
  return <ConsentBanner />;
}

export default App;
